import axios from "axios";
import { API_URL } from "../config";
import {
  getAuthToken,
  isTokenExpired,
  forceLogout,
} from "../helpers/auth-helpers";

// Create an Axios instance with a base URL
const api = axios.create({
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add a request interceptor to add the auth token to requests and check expiration
api.interceptors.request.use(
  (config) => {
    const token = getAuthToken();

    if (token) {
      // Check if token is expired before making the request
      if (isTokenExpired(token)) {
        console.warn("Token expired, forcing logout...");
        forceLogout();
        return Promise.reject(new Error("Token expired"));
      }

      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error),
);

// Add a response interceptor to handle authentication errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      const { status, data } = error.response;

      // Handle authentication errors
      if (status === 401) {
        console.error(
          "Authentication failed:",
          data?.message || "Unauthorized",
        );

        // Check if it's specifically a token expiration error
        const isTokenExpiredError =
          data?.message?.toLowerCase().includes("token") &&
          (data?.message?.toLowerCase().includes("expired") ||
            data?.message?.toLowerCase().includes("invalid"));

        if (isTokenExpiredError || data?.code === "TOKEN_EXPIRED") {
          console.log("Token expired according to server response");
          forceLogout();
        } else {
          // Other auth errors, still force logout
          forceLogout();
        }
      }

      // Handle forbidden errors
      if (status === 403) {
        console.error("Access forbidden:", data?.message || "Forbidden");
        // You might want to redirect to a "no access" page instead of login
        // For now, we'll also force logout
        forceLogout();
      }
    }

    return Promise.reject(error);
  },
);

export default api;
